<template>
  <!--
    UserManager — the User_Manager container.

    Mounted inside the Admin_Shell via the `/admin/users` child route, this
    container hosts the User_List and owns the cross-cutting concerns above it:
    the success / error snackbars for the outcomes of user actions (create,
    update, role change, delete) and the fetched account collection.

    Mirrors ProjectManager.vue exactly, including the `?saved=` query handshake:
    the User_Editor is a sibling route, so it navigates back here with a flag and
    this container raises the matching confirmation on arrival.
  -->
  <div class="user-manager">
    <div class="user-manager__header">
      <div>
        <h2 class="user-manager__heading">Manage users</h2>
        <p class="user-manager__subheading">
          Administrators can manage users and projects. Employees can sign in but
          cannot manage accounts.
        </p>
      </div>
      <v-btn
        color="#e7511e"
        prepend-icon="mdi-account-plus"
        class="text-none"
        data-test="add-user"
        @click="goToCreate"
      >
        Add user
      </v-btn>
    </div>

    <UserList
      ref="list"
      @loaded="onUsersLoaded"
      @add="goToCreate"
      @edit="goToEdit"
      @role-changed="onActionSuccess"
      @deleted="onActionSuccess"
      @error="onActionError"
    />

    <v-snackbar
      v-model="successSnackbar.open"
      color="success"
      :timeout="SNACKBAR_TIMEOUT"
      location="top"
      data-test="success-snackbar"
    >
      {{ successSnackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="successSnackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="errorSnackbar.open"
      color="error"
      :timeout="SNACKBAR_TIMEOUT"
      location="top"
      data-test="error-snackbar"
    >
      {{ errorSnackbar.message }}
      <template #actions>
        <v-btn variant="text" @click="errorSnackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
// UserManager — User_Manager container.
import UserList from 'components/admin/UserList.vue'

const SNACKBAR_TIMEOUT = 4000

// Success messages keyed by the `saved` query flag the editor appends when it
// returns to this route after a successful create / update.
const SAVED_MESSAGES = {
  created: 'User created.',
  updated: 'User updated.'
}

export default {
  name: 'UserManager',

  components: {
    UserList
  },

  data () {
    return {
      SNACKBAR_TIMEOUT,

      users: [],

      successSnackbar: {
        open: false,
        message: ''
      },

      errorSnackbar: {
        open: false,
        message: ''
      }
    }
  },

  mounted () {
    this.handleSavedQuery()
  },

  watch: {
    '$route.query.saved' () {
      this.handleSavedQuery()
    }
  },

  methods: {
    onUsersLoaded (users) {
      this.users = Array.isArray(users) ? users : []
    },

    goToCreate () {
      this.$router.push({ name: 'admin-user-new' })
    },

    goToEdit (id) {
      if (id == null) return
      this.$router.push({ name: 'admin-user-edit', params: { id: String(id) } })
    },

    showSuccess (message) {
      this.successSnackbar.message =
        typeof message === 'string' && message ? message : 'Done.'
      this.successSnackbar.open = true
    },

    showError (message) {
      this.errorSnackbar.message =
        typeof message === 'string' && message
          ? message
          : 'Something went wrong. Please try again.'
      this.errorSnackbar.open = true
    },

    onActionSuccess (payload) {
      this.showSuccess(payload && payload.message)
    },

    onActionError (payload) {
      this.showError(payload && payload.message)
    },

    // Show the confirmation matching a `saved` flag left by the editor, then
    // strip the flag so a refresh does not repeat the message.
    handleSavedQuery () {
      const saved = this.$route && this.$route.query && this.$route.query.saved
      const message = SAVED_MESSAGES[saved]
      if (!message) return

      this.showSuccess(message)

      const query = { ...this.$route.query }
      delete query.saved
      this.$router.replace({ query }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/*
  Clear the fixed Vuetify v-app-bar, matching ProjectManager. The app shell
  renders page content in a plain <main> (not <v-main>), so content is not
  automatically offset beneath the app bar.
*/
.user-manager {
  padding: 64px 24px 48px;
}

.user-manager__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.user-manager__heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #41281b;
}

.user-manager__subheading {
  font-size: 0.875rem;
  color: #4a4a4a;
  margin-top: 4px;
  max-width: 60ch;
}
</style>
